import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { User, UserDetails, UserLogin, UserVisibility } from '../models/user';

const GET_CONFIRM_PASSWORD_PATH = '/rest/auth/confirmPassword';
const GET_CONNECTION_PATH = '/login-legacy';
const GET_USER_PATH = '/rest/auth/getUserBySessionToken';
const GET_SEARCH_VISIBILITY = '/rest/v1/users:searchVisibility';
const GET_SEARCH_ORGA_VISIBILITY = '/rest/v1/users:searchOrganizationVisibility';
const GET_USER_BY_SESSION = '/rest/v2/getUserBySessionToken';
const V2_SERVICES = '/rest/v2/';
const GET_FORGOT_PASSWORD_URL = '/rest/v1/email/forgotPassword';
const AUTH_URL = '/rest/v3';


@Injectable({ providedIn: 'root' })
export class UserService {
	constructor(
		private readonly http: HttpClient
	) {}

	getConfirmPasswordUrl(token?: string) {
		const confirmUrl = new URL(GET_CONFIRM_PASSWORD_PATH, location.origin);
		const params = confirmUrl.searchParams;
		if (token) {
			params.set('session_token', token);
		}
		return confirmUrl;
	}

	getUser(token: string) {
		const sessionToken = encodeURIComponent(token);
		const url = new URL(`${ GET_USER_PATH }/${ sessionToken }`, location.origin);
		return this.http.get<User>(url.toString());
	}

	getConnectionUrl() {
		return new URL(GET_CONNECTION_PATH, location.origin);
	}

	searchVisibility(query: string, page: number, limit: number) {
		const url = new URL(GET_SEARCH_VISIBILITY, location.origin);
		url.searchParams.append('query', query);
		url.searchParams.append('page', String(page));
		url.searchParams.append('limit', String(limit));
		return this.http.get<{ results: UserVisibility[]; hasDelegation: boolean }>(url.toString());
	}

	searchOrgaVisibility(query: string, page: number, limit: number) {
		const url = new URL(GET_SEARCH_ORGA_VISIBILITY, location.origin);
		url.searchParams.append('query', query);
		url.searchParams.append('page', String(page));
		url.searchParams.append('limit', String(limit));
		return this.http.get<{ results: UserVisibility[]; hasSimulation: boolean }>(url.toString());
	}

	readCookie(cookieName: string) {
		const nameEQ = cookieName.concat('=');
		const ca = document.cookie.split(';');
		for (let c of ca) {
			while (c.startsWith(' ')) {
				c = c.slice(1);
			}
			if (c.startsWith(nameEQ)) {
				return c.slice(nameEQ.length, c.length);
			}
		}
		return '';
	}

	getUserByToken(token: string) {
		const sessionToken = encodeURIComponent(token);
		const url = new URL(`${ GET_USER_BY_SESSION }/${ sessionToken }`, location.origin);
		return this.http.get<UserDetails>(url.toString());
	}

	async createLogin(updatedUser: UserLogin, sessionToken: string) {
		const url = `${ V2_SERVICES }validate-user/${ sessionToken }`;
		await firstValueFrom(this.http.put(url.toString(), updatedUser));
	}

	async confirmPassword(sessionToken: string, password: string) {
		const url = this.confirmPasswordUrl(sessionToken);
		await firstValueFrom(this.http.post(url.toString(), password));
	}

	confirmPasswordUrl(sessionToken: string) {
		return this.getConfirmPasswordUrl(sessionToken);
	}

	async updateEmail(sessionToken: string) {
		const url = `${ V2_SERVICES }update-email/${ sessionToken }`;
		return firstValueFrom(this.http.put(url, null, { responseType: 'text' }));
	}

	async resetPassword(userEmail: string, isNewPortal: string) {
		const url = GET_FORGOT_PASSWORD_URL;
		const body = new URLSearchParams();
		body.set('userEmail', userEmail);
		body.set('isNewPortal', isNewPortal);
		await firstValueFrom(this.http.post(
			url.toString(),
			body.toString(),
			{
				headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
			}
		));
	}

	accessUrl() {
		return this.getRedirectionUrl();
	}

	getRedirectionUrl() {
		return new URL('/login', location.origin);
	}

	async updateUserPassword(oldPwd: string, newPwd: string, confNewPwd: string, token?: string) {
		const url = new URL(`${ AUTH_URL }/updateExpiredPassword`, location.origin);

		const passwords = {
			oldPwd,
			newPwd,
			confNewPwd,
		};
		const params = url.searchParams;
		if (token) {
			params.set('session_token', token);
		}
		await firstValueFrom(this.http.post(url.toString(), passwords));
	}
}
